import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createOrder,
  getShippingQuote,
  type CreateOrderRequest,
  type CreateOrderResponse,
  type ShippingQuoteResponse,
} from "./api-client";
import { CART_QUERY_KEY } from "./cart";

/**
 * Tunnel de commande — état partagé TanStack Query. Porté depuis
 * apps/web/lib/checkout-actions.ts (Server Actions `getShippingQuoteAction`/
 * `placeOrderAction`), en `useQuery`/`useMutation` : plus de distinction
 * Server Action/Client Component, `CheckoutForm` (src/components/cart/) les
 * appelle directement.
 */

/**
 * Devis de livraison (§8.2 : « les frais s'affichent dès la commune
 * choisie »). `enabled: false` tant qu'aucune commune n'est sélectionnée —
 * reproduit `if (!districtId) return { quote: null, error: null }` de
 * l'action d'origine sans avoir besoin d'un premier appel réseau à vide.
 */
export function useShippingQuote(districtId: string) {
  return useQuery<ShippingQuoteResponse>({
    queryKey: ["checkout", "shipping-quote", districtId],
    queryFn: () => getShippingQuote({ districtId }),
    enabled: districtId.length > 0,
    staleTime: 60_000,
    retry: 0,
  });
}

/**
 * Données nécessaires à la page de confirmation, produites par
 * `CheckoutForm` au succès de `usePlaceOrder` et transmises via
 * `navigate("/commande/confirmation", { state })` (cf. CommandePage.tsx pour
 * la justification de `location.state` plutôt qu'une query string).
 */
export interface PlaceOrderResult {
  order: CreateOrderResponse;
  recipientName: string;
  districtName: string;
  shippingMethodLabel: string;
  itemCount: number;
}

export interface PlaceOrderVariables {
  payload: CreateOrderRequest;
  /**
   * Généré une fois par tentative de commande côté appelant (`crypto.randomUUID()`,
   * cf. `CheckoutForm`) et réutilisé pour toute nouvelle tentative de la même
   * commande — jamais régénéré ici, sinon `POST /checkout/orders` ne serait
   * plus idempotent (§11.1).
   */
  idempotencyKey: string;
}

/**
 * Validation de la commande (§8.2). Contrairement aux autres mutations panier
 * (`src/lib/cart.ts`), n'invalide `CART_QUERY_KEY` QU'au succès : la commande
 * créée vide le panier associé côté API (statut `CONVERTED`), donc seul un
 * succès rend l'ancien état de panier obsolète — un échec (ex. stock manquant)
 * laisse le panier tel quel, pas besoin de le refetch.
 */
export function usePlaceOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, idempotencyKey }: PlaceOrderVariables) => createOrder(payload, idempotencyKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
    },
  });
}
